import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Container,
    Grid,
    Typography,
    TextField,
    Button,
    LinearProgress,
    Card,
    CardContent,
    Chip,
    InputAdornment
} from '@mui/material';
import theme, { textClasses, buttonClasses } from '../../../static/styles/theme';
import EmailIcon from '@mui/icons-material/Email';
import LynkitLogo from '../../../static/images/login/logo-white.svg';
import Cookies from 'js-cookie';
import axios, { resetMaintenanceFlag } from '../../../services/Api';
import history from '../../../services/history';

const MaintenancePage = () => {
    const centralTheme = localStorage.getItem("centralTheming")
        ? JSON.parse(localStorage.getItem("centralTheming"))
        : {};   
    const logoUrl = centralTheme?.sidebarLogoUrl || LynkitLogo;
    const [email, setEmail] = useState('');
    const [countdown, setCountdown] = useState({
        hours: 0,
        minutes: 0,
        seconds: 0
    });
    const [openedAt] = useState(() => new Date());
    const [incidentNumber] = useState(() => {
        const now = new Date();
        const start = new Date(now.getFullYear(), 0, 0);
        const dayOfYear = Math.floor((now - start) / (1000 * 60 * 60 * 24));
        return String(dayOfYear).padStart(3, '0');
    });
    const [retryCountdown, setRetryCountdown] = useState(20);
    const [activeIncidentIndex, setActiveIncidentIndex] = useState(0);
    const [progress, setProgress] = useState(0);
    const healthCheckRef = useRef(null);
    const countdownIntervalRef = useRef(null);

    const formatDate = (date) => {
        const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
        const dd = String(date.getDate()).padStart(2, '0');
        const mmm = months[date.getMonth()];
        const yyyy = date.getFullYear();
        const hh = String(date.getHours()).padStart(2, '0');
        const mm = String(date.getMinutes()).padStart(2, '0');
        return `${dd} ${mmm} ${yyyy} - ${hh}:${mm} IST`;
    };

    // Incidents ordered from latest to earliest
    // revealAt = minutes after page open when this incident card appears
    const allIncidents = [
        {
            id: 1,
            revealAt: 100,  // appears at ~100 min
            title: 'Fix deployed to staging.',
            description: 'Load test running. ETA to production rollout: 90 minutes.',
            status: 'Fixing',
            color: '#3B82F6'
        },
        {
            id: 2,
            revealAt: 80,   // appears at ~80 min
            title: 'Database query optimization in progress.',
            description: 'Engineers are patching the root cause of the API timeout spike.',
            status: 'Fixing',
            color: '#3B82F6'
        },
        {
            id: 3,
            revealAt: 60,   // appears at ~60 min
            title: 'Root cause identified.',
            description: 'Unindexed query in the inventory reconciliation module causing cascading timeouts under peak load.',
            status: 'Identified',
            color: '#F59E0B'
        },
        {
            id: 4,
            revealAt: 40,   // appears at ~40 min
            title: 'Rollback of the deployment initiated.',
            description: 'Services partially restored — login and dashboard accessible. Scanning dispatch still impacted.',
            status: 'Investigating',
            color: '#EF4444'
        },
        {
            id: 5,
            revealAt: 20,   // appears at ~20 min
            title: 'Incident escalated to senior engineering.',
            description: 'All scheduled deployments frozen. War room initiated.',
            status: 'Investigating',
            color: '#EF4444'
        },
        {
            id: 6,
            revealAt: 0,    // visible immediately
            title: 'Incident detected.',
            description: 'Automated monitoring alerted on API error rate exceeding threshold. DevOps team engaged.',
            status: 'Investigating',
            color: '#EF4444'
        }
    ];

    // Mock data for affected services
    const affectedServices = [
        { name: 'Warehouse Scanning (WMS Core)', status: 'Down', color: '#EF4444' },
        { name: 'Dispatch & Outbound', status: 'Down', color: '#EF4444' },
        { name: 'Inbound GRN', status: 'Degraded', color: '#F59E0B' },
        { name: 'Dashboard & Reports', status: 'Operational', color: '#10B981' },
        { name: 'API / Integrations', status: 'Degraded', color: '#F59E0B' },
        { name: 'User Authentication', status: 'Operational', color: '#10B981' }
    ];

    // All incidents with pre-computed dates (all visible from start)
    const incidentList = allIncidents.map(inc => ({
        ...inc,
        date: formatDate(new Date(openedAt.getTime() + inc.revealAt * 60 * 1000))
    }));

    // Track active incident & progress bar over 2 hours (120 min)
    useEffect(() => {
        const totalDuration = 2 * 60 * 60 * 1000; // 2 hours in ms

        const updateState = () => {
            const elapsed = Date.now() - openedAt.getTime();
            const elapsedMinutes = elapsed / (60 * 1000);

            // Find the latest reached incident (they are ordered newest-first, so find last whose revealAt <= elapsed)
            // allIncidents is sorted descending by revealAt, so we find the first match
            let activeIdx = allIncidents.length - 1; // default to last (oldest)
            for (let i = 0; i < allIncidents.length; i++) {
                if (elapsedMinutes >= allIncidents[i].revealAt) {
                    activeIdx = i;
                    break;
                }
            }
            setActiveIncidentIndex(activeIdx);

            // Progress: 0% to 100% over 2 hours
            const pct = Math.min(Math.round((elapsed / totalDuration) * 100), 100);
            setProgress(pct);
        };

        updateState();
        const interval = setInterval(updateState, 30000);
        return () => clearInterval(interval);
    }, []);

    // Countdown timer effect
    useEffect(() => {
        const targetTime = new Date().getTime() + (2 * 60 * 60 * 1000); // 2 hours from now

        const interval = setInterval(() => {
            const now = new Date().getTime();
            const distance = targetTime - now;

            if (distance < 0) {
                clearInterval(interval);
                setCountdown({ hours: 0, minutes: 0, seconds: 0 });
            } else {
                const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((distance % (1000 * 60)) / 1000);

                setCountdown({ hours, minutes, seconds });
            }
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    // Health check with increasing intervals: 30s, 1m, 2m, 5m, 10m, 30m (repeats 30m)
    useEffect(() => {
        const retryDelays = [30, 60, 120, 300, 600, 1800];
        let attempt = 0;
        let cancelled = false;
        let isChecking = false;
        let abortController = null;

        const checkHealth = () => {
            // Prevent overlapping calls
            if (isChecking) return;
            isChecking = true;

            // Abort if health API takes longer than 3 seconds
            abortController = new AbortController();
            const timeoutId = setTimeout(() => abortController.abort(), 3000);

            axios.get('/health/get', { signal: abortController.signal })
                .then(({ data }) => {
                    clearTimeout(timeoutId);
                    isChecking = false;
                    if (data.success) {
                        cancelled = true;
                        clearInterval(countdownIntervalRef.current);
                        resetMaintenanceFlag();
                        history.push('/dashboard');
                    } else {
                        scheduleNext();
                    }
                })
                .catch(() => {
                    clearTimeout(timeoutId);
                    isChecking = false;
                    scheduleNext();
                });
        };

        const scheduleNext = () => {
            if (cancelled) return;
            attempt++;
            const delay = attempt < retryDelays.length ? retryDelays[attempt] : retryDelays[retryDelays.length - 1];
            setRetryCountdown(delay);

            healthCheckRef.current = setTimeout(() => {
                if (!cancelled) checkHealth();
            }, delay * 1000);
        };

        // Countdown ticker — ticks every second
        countdownIntervalRef.current = setInterval(() => {
            setRetryCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
        }, 1000);

        // First call after 30 seconds
        setRetryCountdown(30);
        healthCheckRef.current = setTimeout(() => {
            if (!cancelled) checkHealth();
        }, 30000);

        return () => {
            cancelled = true;
            if (abortController) abortController.abort();
            clearTimeout(healthCheckRef.current);
            clearInterval(countdownIntervalRef.current);
        };
    }, []);

    const handleNotifyMe = () => {
        if (email) {
            console.log('Notification email registered:', email);
            // Add your notification logic here
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                backgroundColor: '#0A0A0A',
                color: theme.themeWhite,
                py: 4,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: `radial-gradient(circle, #333 1px, transparent 1px), radial-gradient(circle, ${theme.themeOrange} 1px, transparent 1px)`,
                    backgroundSize: '40px 40px, 200px 200px',
                    backgroundPosition: '0 0, 20px 20px',
                    opacity: 0.4,
                    zIndex: 0,
                },
                '& > *': {
                    position: 'relative',
                    zIndex: 1,
                },
            }}
        >
            <Container maxWidth="xl">
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <img src={logoUrl} height="40" alt="Logo" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = LynkitLogo; }} />
                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                            {/* Retry Countdown */}
                            <Typography sx={{ fontSize: '13px', color: theme.themeGray }}>
                                {retryCountdown > 0
                                    ? <>Retrying in <span style={{ color: theme.themeOrange, fontWeight: 'bold' }}>
                                        {retryCountdown >= 60
                                            ? `${Math.floor(retryCountdown / 60)}m ${retryCountdown % 60 ? `${retryCountdown % 60}s` : ''}`
                                            : `${retryCountdown}s`
                                        }
                                    </span></>
                                    : <span style={{ color: theme.themeOrange }}>Checking...</span>
                                }
                            </Typography>
                            <Box
                                sx={{
                                    border: `1px solid ${theme.themeOrange}`,
                                    borderRadius: '20px',
                                    px: 2,
                                    py: 0.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                {/* Blinking Dot */}
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: theme.themeOrange,
                                        animation: 'blink 1.5s ease-in-out infinite',
                                        boxShadow: `0 0 10px ${theme.themeOrange}`,
                                        '@keyframes blink': {
                                            '0%, 100%': {
                                                opacity: 1,
                                                transform: 'scale(1)'
                                            },
                                            '50%': {
                                                opacity: 0.3,
                                                transform: 'scale(0.8)'
                                            }
                                        }
                                    }}
                                />
                                <Typography sx={{ ...textClasses.normalText, color: theme.themeOrange, letterSpacing: '1px' }}>
                                    MAINTENANCE IN PROGRESS
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Grid container spacing={3}>
                    {/* Left Section */}
                    <Grid item xs={12} lg={8} sx={{ position: 'relative', overflow: 'hidden' }}>
                        {/* Scanner Line Animation */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '2px',
                                background: `linear-gradient(90deg,
                                    transparent 0%,
                                    ${theme.themeOrange} 50%,
                                    transparent 100%)`,
                                boxShadow: `0 0 20px ${theme.themeOrange}, 0 0 40px ${theme.themeOrange}`,
                                animation: 'scan 3s linear infinite',
                                zIndex: 10,
                                '@keyframes scan': {
                                    '0%': {
                                        transform: 'translateY(0)',
                                        opacity: 0
                                    },
                                    '10%': {
                                        opacity: 1
                                    },
                                    '90%': {
                                        opacity: 1
                                    },
                                    '100%': {
                                        transform: 'translateY(100vh)',
                                        opacity: 0
                                    }
                                }
                            }}
                        />
                        {/* Incident Header */}
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                                <Typography sx={{ fontSize: '14px', color: theme.themeGray }}>
                                    INC - <span style={{ color: theme.themeOrange }}>#LKG-{openedAt.getFullYear()}-{incidentNumber}</span>
                                </Typography>
                                <Typography sx={{ fontSize: '14px', color: theme.themeGray }}>
                                    OPENED {formatDate(openedAt).replace(' - ', ', ')}
                                </Typography>
                            </Box>

                            <Typography
                                sx={{
                                    fontSize: { xs: '32px', md: '48px' },
                                    fontWeight: 'bold',
                                    lineHeight: 1.2,
                                    mb: 2
                                }}
                            >
                                SYSTEM <br />
                                <span style={{ color: theme.themeOrange }}>MAINTENANCE</span> <br />
                                UNDERWAY
                            </Typography>

                            <Typography sx={{ fontSize: '16px', color: theme.themeGray,}}>
                                Our engineering team is actively working to restore full{' '}
                                <span style={{ fontWeight: 'bold', color: theme.themeWhite }}>Lynkgrid WMS</span>
                            </Typography>
                            <Typography sx={{ fontSize: '16px', color: theme.themeGray }}>
                                operations. Warehouse scanning, dispatch, and inbound receiving are
                            </Typography>
                            <Typography sx={{ fontSize: '16px', color: theme.themeGray }}>
                                temporarily unavailable.{' '}
                                <span style={{ fontWeight: 'bold', color: theme.themeWhite }}>All your data is safe</span>{' '}
                                — no transactions have been lost.
                            </Typography>
                        </Box>

                        {/* Countdown Timer */}
                        <Box sx={{ mb: 4 }}>
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    color: theme.themeDarkGray,
                                    letterSpacing: '2px',
                                    mb: 2
                                }}
                            >
                                ESTIMATED TIME TO RESTORATION
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 3 }}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '48px', md: '64px' },
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {String(countdown.hours).padStart(2, '0')}
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', color: theme.themeDarkGray }}>HOURS</Typography>
                                </Box>
                                <Typography sx={{ fontSize: { xs: '48px', md: '64px' }, fontWeight: 'bold' }}>
                                    :
                                </Typography>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '48px', md: '64px' },
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {String(countdown.minutes).padStart(2, '0')}
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', color: theme.themeDarkGray }}>MINUTES</Typography>
                                </Box>
                                <Typography sx={{ fontSize: { xs: '48px', md: '64px' }, fontWeight: 'bold' }}>
                                    :
                                </Typography>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '48px', md: '64px' },
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {String(countdown.seconds).padStart(2, '0')}
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', color: theme.themeDarkGray }}>SECONDS</Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Progress Bar */}
                        <Box sx={{ mb: 4 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography sx={{ fontSize: '14px', color: theme.themeDarkGray, letterSpacing: '2px' }}>
                                    Restoration Progress
                                </Typography>
                                <Typography sx={{ fontSize: '14px', color: theme.themeOrange, fontWeight: 'bold' }}>
                                    {progress}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: theme.themeShadow,
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: theme.themeOrange,
                                        borderRadius: 4
                                    }
                                }}
                            />
                        </Box>

                        {/* Contact Info */}
                        <Box
                            sx={{
                                backgroundColor: '#111111',
                                border: '1px solid #222',
                                borderRadius: '8px',
                                p: 3
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    mb: 2,
                                    color: theme.themeDarkGray,
                                    letterSpacing: '2px'
                                }}
                            >
                                NEED IMMEDIATE HELP?
                            </Typography>
                            <Box
                                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, cursor: 'pointer' }}
                                onClick={() => window.open('https://mail.google.com/mail/?view=cm&to=wms-support@lynkgrid.com', '_blank')}
                            >
                                <EmailIcon sx={{ color: theme.themeDarkGray, fontSize: '18px' }} />
                                <Typography sx={{ fontSize: '14px', color: theme.themeGray, '&:hover': { color: theme.themeOrange } }}>
                                    wms-support@lynkgrid.com
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <Box sx={{ fontSize: '18px', lineHeight: 1 }}>🌐</Box>
                                <Typography
                                    component="a"
                                    href="https://lynkit.in/ContactUs"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ fontSize: '14px', color: theme.themeGray, textDecoration: 'none', '&:hover': { color: theme.themeOrange } }}
                                >
                                    Contact Us 
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ fontSize: '18px', lineHeight: 1 }}>📞</Box>
                                <Typography sx={{ fontSize: '14px', color: theme.themeGray }}>
                                    +91 11 4082 4028
                                </Typography>
                            </Box>
                        </Box>

                        {/* Notification Form - Commented Out */}
                        {/* <Box
                            sx={{
                                backgroundColor: '#111111',
                                border: '1px solid #222',
                                borderRadius: '8px',
                                p: 3,
                                mt: 3
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    color: theme.themeDarkGray,
                                    letterSpacing: '2px',
                                    mb: 2
                                }}
                            >
                                NOTIFY ME WHEN SERVICE IS RESTORED
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <TextField
                                    placeholder="your@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    sx={{
                                        flex: 1,
                                        minWidth: '250px',
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#0A0A0A',
                                            color: theme.themeWhite,
                                            borderRadius: '8px',
                                            '& fieldset': {
                                                borderColor: '#333'
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme.themeOrange
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme.themeOrange
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            color: theme.themeWhite
                                        },
                                        '& .MuiInputBase-input::placeholder': {
                                            color: theme.themeDarkGray,
                                            opacity: 1
                                        }
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailIcon sx={{ color: theme.themeDarkGray }} />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                <Button
                                    onClick={handleNotifyMe}
                                    sx={{
                                        ...buttonClasses.lynkitOrangeFill,
                                        px: 4,
                                        py: 1.5,
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        textTransform: 'uppercase'
                                    }}
                                >
                                    Notify Me
                                </Button>
                            </Box>
                        </Box> */}
                    </Grid>

                    {/* Right Section */}
                    {false && (
                    <Grid item xs={12} lg={4}>
                        {/* Live Incident Feed */}
                        <Box sx={{ mb: 4 }}>
                            <Typography
                                sx={{
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    mb: 2,
                                    letterSpacing: '1px'
                                }}
                            >
                                LIVE INCIDENT FEED
                            </Typography>
                            {/* Timeline Container */}
                            <Box sx={{ position: 'relative', pl: 2 }}>
                                {incidentList.map((incident, index) => (
                                    <Box
                                        key={incident.id}
                                        sx={{
                                            position: 'relative',
                                            pb: index < incidentList.length - 1 ? 3 : 0
                                        }}
                                    >
                                        {/* Timeline Line */}
                                        {index < incidentList.length - 1 && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    left: 3,
                                                    top: 20,
                                                    bottom: -12,
                                                    width: '2px',
                                                    backgroundColor: '#333',
                                                    zIndex: 0
                                                }}
                                            />
                                        )}

                                        {/* Timeline Item */}
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            {/* Timeline Dot */}
                                            <Box
                                                sx={{
                                                    position: 'relative',
                                                    zIndex: 1,
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    pt: 1.5
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: '50%',
                                                        backgroundColor: index < activeIncidentIndex ? '#555' : incident.color,
                                                        border: '2px solid #0A0A0A',
                                                        boxShadow: index === activeIncidentIndex ? `0 0 10px ${incident.color}` : 'none',
                                                        ...(index === activeIncidentIndex && {
                                                            animation: 'dotBlink 1.5s ease-in-out infinite',
                                                            '@keyframes dotBlink': {
                                                                '0%, 100%': {
                                                                    opacity: 1,
                                                                    transform: 'scale(1.2)',
                                                                    boxShadow: `0 0 12px ${incident.color}`,
                                                                },
                                                                '50%': {
                                                                    opacity: 0.4,
                                                                    transform: 'scale(0.8)',
                                                                    boxShadow: 'none',
                                                                }
                                                            }
                                                        }),
                                                    }}
                                                />
                                            </Box>

                                            {/* Card Content */}
                                            <Card
                                                sx={{
                                                    flex: 1,
                                                    backgroundColor: '#111111',
                                                    border: index === activeIncidentIndex ? `1px solid ${incident.color}` : '1px solid #222',
                                                    borderRadius: '8px',
                                                    boxShadow: 'none',
                                                    opacity: index < activeIncidentIndex ? 0.4 : 1,
                                                    transition: 'opacity 0.5s ease, border 0.5s ease',
                                                }}
                                            >
                                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                    <Typography sx={{ fontSize: '11px', color: theme.themeDarkGray, mb: 0.5 }}>
                                                        {incident.date}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '14px',
                                                            fontWeight: 'bold',
                                                            color: theme.themeWhite,
                                                            mb: 0.5
                                                        }}
                                                    >
                                                        {incident.title}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '12px', color: theme.themeGray, mb: 1 }}>
                                                        {incident.description}
                                                    </Typography>
                                                    <Chip
                                                        label={incident.status}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: incident.color,
                                                            color: theme.themeWhite,
                                                            fontSize: '10px',
                                                            fontWeight: 'bold',
                                                            height: '18px'
                                                        }}
                                                    />
                                                </CardContent>
                                            </Card>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Grid>
                    )}
                </Grid>

                {/* Footer */}
                <Box
                    sx={{
                        mt: 6,
                        pt: 3,
                        borderTop: '1px solid #222',
                        textAlign: 'center'
                    }}
                >
                    <Typography sx={{ fontSize: '12px', color: theme.themeDarkGray }}>
                        Copyright © {new Date().getFullYear()} Lynkit. All Rights Reserved
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default MaintenancePage;
